import {
  CUSTOMER_DOMAIN,
  SCAFFOLDER_DOMAIN,
  type SiteKind,
} from "@/lib/sites/domains";
import { RESERVED_SCAFFOLDER_SUBDOMAINS } from "@/lib/white-label/constants";

export type HostKind =
  | "platform-scaffolder"
  | "platform-scaffolder-app"
  | "platform-customer"
  | "tenant-scaffer-subdomain"
  | "tenant-custom-domain"
  | "unknown";

export function normalizeHostname(host: string | null | undefined): string {
  if (!host) return "";
  return host.toLowerCase().split(":")[0].trim().replace(/\.$/, "");
}

function apex(hostname: string): string {
  return hostname.split(":")[0].toLowerCase().replace(/\.$/, "");
}

function hostMatchesApex(hostname: string, domain: string): boolean {
  const h = apex(hostname);
  const a = apex(domain);
  if (!a) return false;
  return h === a || h === `www.${a}` || h.endsWith(`.${a}`);
}

export function getScaffolderApex(): string {
  return apex(SCAFFOLDER_DOMAIN);
}

export function getCustomerApex(): string {
  return apex(CUSTOMER_DOMAIN);
}

export function isPlatformScaffolderHost(hostname: string): boolean {
  const h = normalizeHostname(hostname);
  if (!h || h === "localhost" || h === "127.0.0.1") return false;
  const apex = getScaffolderApex();
  return h === apex || h === `www.${apex}`;
}

export function isPlatformCustomerHost(hostname: string): boolean {
  const h = normalizeHostname(hostname);
  if (!h || h === "localhost" || h === "127.0.0.1") return false;
  const apex = getCustomerApex();
  return h === apex || h === `www.${apex}` || h.endsWith(`.${apex}`);
}

export function isScaffolderAppHost(hostname: string): boolean {
  const h = normalizeHostname(hostname);
  return h === `app.${getScaffolderApex()}`;
}

/** e.g. ads.scaffer.co.uk → "ads" */
export function extractScafferTenantSubdomain(hostname: string): string | null {
  const h = normalizeHostname(hostname);
  const apex = getScaffolderApex();
  if (!h.endsWith(`.${apex}`) || h === apex || h === `www.${apex}`) return null;
  const sub = h.slice(0, -(apex.length + 1));
  if (!sub || sub.includes(".") || RESERVED_SCAFFOLDER_SUBDOMAINS.has(sub)) return null;
  return sub;
}

export function classifyHost(hostname: string): HostKind {
  const h = normalizeHostname(hostname);
  if (!h || h === "localhost" || h === "127.0.0.1") {
    return process.env.NEXT_PUBLIC_SITE === "scaffolder"
      ? "platform-scaffolder"
      : "platform-customer";
  }

  if (isScaffolderAppHost(h)) return "platform-scaffolder-app";
  if (isPlatformScaffolderHost(h)) return "platform-scaffolder";

  const sub = extractScafferTenantSubdomain(h);
  if (sub) return "tenant-scaffer-subdomain";

  if (isPlatformCustomerHost(h)) return "platform-customer";

  return "tenant-custom-domain";
}

export function isTenantPortalHost(hostname: string): boolean {
  const kind = classifyHost(hostname);
  return kind === "tenant-scaffer-subdomain" || kind === "tenant-custom-domain";
}

export function siteKindFromHost(hostname: string): SiteKind | "tenant-portal" {
  const kind = classifyHost(hostname);
  if (kind === "platform-scaffolder" || kind === "platform-scaffolder-app") return "scaffolder";
  if (kind === "tenant-scaffer-subdomain" || kind === "tenant-custom-domain") return "tenant-portal";
  return "customer";
}

export function txtVerificationHostname(customDomain: string): string {
  const host = normalizeHostname(customDomain);
  return `${"_scaffer-verification"}.${host}`;
}

export function txtVerificationRecordName(customDomain: string): string {
  const parts = normalizeHostname(customDomain).split(".");
  if (parts.length < 2) return "_scaffer-verification";
  const subdomain = parts[0];
  return `_scaffer-verification.${subdomain}`;
}

export function cnameRecordName(customDomain: string): string {
  const parts = normalizeHostname(customDomain).split(".");
  if (parts.length < 2) return "@";
  return parts[0];
}
